import { baseApi } from "@/shared/api";
import type { CancelSigningArgs, CancelSigningResponse } from "../types";
import type {
  CreatedSigningOperation,
  MobileLaunch,
  OfficialCopyResponse,
  SigningClient,
  SigningDocumentDetailV2,
  SigningOperation,
  SigningOperationWithLinks,
} from "../types/mobileSigning";

interface DomainLaunchArgs {
  url: string;
  client?: SigningClient;
  /** Extra payload for create-and-sign endpoints that build the document first. */
  body?: Record<string, unknown>;
}

interface CreateOperationArgs {
  signingDocumentId?: string;
  resource?: { type: string; id: number };
  client?: SigningClient;
}

interface LegalLaunchEnvelope {
  success: boolean;
  data: MobileLaunch;
}

function normalizeLaunch(response: MobileLaunch | LegalLaunchEnvelope): MobileLaunch {
  if ("data" in response && response.data) return response.data;
  return response as MobileLaunch;
}

export const mobileSigningApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    startDomainMobileSigning: builder.mutation<MobileLaunch, DomainLaunchArgs>({
      query: ({ url, client = "web", body }) => ({
        url,
        method: "POST",
        body: { ...(body ?? {}), client },
        credentials: "include" as const,
      }),
      transformResponse: normalizeLaunch,
    }),

    /**
     * V1 compatibility adapter: the only launch path keyed by the provider
     * document id, which is what counter-sign screens have. Returns links
     * directly, like the domain endpoints.
     */
    startMobileCounterSigning: builder.mutation<MobileLaunch, string>({
      query: (documentId) => ({
        url: `/api/signing/mobile/counter-sign/${documentId}/`,
        method: "POST",
        credentials: "include" as const,
      }),
    }),

    abortMobileSigning: builder.mutation<unknown, string>({
      query: (url) => ({
        url,
        method: "POST",
        credentials: "include" as const,
      }),
    }),

    cancelSigning: builder.mutation<CancelSigningResponse, CancelSigningArgs>({
      query: ({ url }) => ({
        url,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { tags }) => [...tags, "PendingActions"],
    }),

    createSigningOperation: builder.mutation<CreatedSigningOperation, CreateOperationArgs>({
      query: ({ signingDocumentId, resource, client = "web" }) => ({
        url: "/api/signing/v2/operations/",
        method: "POST",
        body: signingDocumentId
          ? { signing_document_id: signingDocumentId, client }
          : { resource, client },
        credentials: "include" as const,
      }),
      transformResponse: (data: SigningOperation, meta: unknown) => ({
        ...data,
        httpStatus: (meta as { response?: Response } | undefined)?.response?.status,
      }),
    }),

    getSigningOperation: builder.query<SigningOperation, string>({
      query: (operationPublicId) => ({
        url: `/api/signing/v2/operations/${operationPublicId}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, operationPublicId) => [
        { type: "SigningOperations" as const, id: operationPublicId },
      ],
    }),

    launchSigningOperation: builder.mutation<SigningOperationWithLinks, string>({
      query: (operationPublicId) => ({
        url: `/api/signing/v2/operations/${operationPublicId}/launch/`,
        method: "GET",
        credentials: "include" as const,
      }),
    }),

    cancelSigningOperation: builder.mutation<SigningOperation, string>({
      query: (operationPublicId) => ({
        url: `/api/signing/v2/operations/${operationPublicId}/cancel/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, operationPublicId) => [
        { type: "SigningOperations" as const, id: operationPublicId },
      ],
    }),

    getSigningDocumentV2: builder.query<SigningDocumentDetailV2, string>({
      query: (signingDocumentPublicId) => ({
        url: `/api/signing/v2/documents/${signingDocumentPublicId}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, signingDocumentPublicId) => [
        { type: "SigningDocumentsV2" as const, id: signingDocumentPublicId },
      ],
    }),

    requestOfficialCopy: builder.mutation<
      OfficialCopyResponse,
      { signingDocumentPublicId: string; locale?: string }
    >({
      query: ({ signingDocumentPublicId, locale = "kk/ru" }) => ({
        url: `/api/signing/v2/documents/${signingDocumentPublicId}/official-copy/`,
        method: "POST",
        body: { locale },
        credentials: "include" as const,
      }),
    }),
  }),
});

export const {
  useStartDomainMobileSigningMutation,
  useStartMobileCounterSigningMutation,
  useAbortMobileSigningMutation,
  useCancelSigningMutation,
  useCreateSigningOperationMutation,
  useGetSigningOperationQuery,
  useLaunchSigningOperationMutation,
  useCancelSigningOperationMutation,
  useGetSigningDocumentV2Query,
  useRequestOfficialCopyMutation,
} = mobileSigningApi;
